import { motion } from "framer-motion";
import { Sparkles, Activity, Zap, BarChart3, Cpu } from "lucide-react";

/**
 * Floating SaaS dashboard / AI workflow mockup used in the hero.
 * Pure CSS + SVG so it stays crisp and lightweight.
 */
export default function HeroVisual() {
  return (
    <div className="relative w-full h-[460px] md:h-[520px]">
      {/* Backdrop glow */}
      <div className="absolute inset-0 bg-hero-radial opacity-80 rounded-3xl" />
      <div className="absolute inset-0 ring-grid opacity-30 rounded-3xl" />

      {/* Main dashboard card */}
      <motion.div
        initial={{ opacity: 0, y: 30, rotateX: 8 }}
        animate={{ opacity: 1, y: 0, rotateX: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="absolute left-4 right-4 top-6 md:left-10 md:right-10 md:top-8 rounded-2xl glass p-4 shadow-card"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
          </div>
          <div className="text-[11px] text-ink-400 font-mono">zolvix.ai · /dashboard</div>
        </div>

        <div className="mt-4 grid grid-cols-12 gap-3">
          <div className="col-span-7 rounded-xl border border-white/[0.06] bg-[#0F2B48]/70 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] text-cyan">MONTHLY REVENUE</div>
                <div className="mt-1 text-2xl font-bold text-white">$1.24M</div>
              </div>
              <div className="text-xs text-emerald-300">▲ 18.6%</div>
            </div>
            <svg viewBox="0 0 300 90" className="mt-3 w-full h-20">
              <defs>
                <linearGradient id="g1" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" stopColor="#16D5FF" stopOpacity="0.5" />
                  <stop offset="1" stopColor="#16D5FF" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d="M0 70 L30 60 L60 65 L90 50 L120 55 L150 35 L180 45 L210 25 L240 30 L270 15 L300 22 L300 90 L0 90 Z" fill="url(#g1)" />
              <path d="M0 70 L30 60 L60 65 L90 50 L120 55 L150 35 L180 45 L210 25 L240 30 L270 15 L300 22" stroke="#16D5FF" strokeWidth="1.5" fill="none" />
            </svg>
          </div>
          <div className="col-span-5 rounded-xl border border-white/[0.06] bg-[#0F2B48]/70 p-4">
            <div className="text-[10px] tracking-[0.2em] text-cyan">AI WORKFLOWS</div>
            <div className="mt-2 space-y-2">
              {["Lead triage", "Invoice OCR", "Support copilot", "Vendor scoring"].map((w, i) => (
                <div key={w} className="flex items-center justify-between text-xs">
                  <span className="text-ink-200">{w}</span>
                  <span className="font-mono text-cyan">{["98%", "94%", "91%", "88%"][i]}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-12 rounded-xl border border-white/[0.06] bg-[#0F2B48]/70 p-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-ink-200">
              <Cpu className="h-4 w-4 text-cyan" /> Pipeline · 12 services
            </div>
            <div className="flex items-center gap-1">
              {Array.from({ length: 28 }).map((_, i) => (
                <span key={i} className="block h-3 w-1 rounded-sm" style={{ background: `rgba(22,213,255,${0.15 + (i % 7) * 0.1})` }} />
              ))}
            </div>
            <div className="text-[11px] font-mono text-ink-400">99.99% uptime</div>
          </div>
        </div>
      </motion.div>

      {/* Floating chips */}
      <motion.div
        initial={{ opacity: 0, x: -30, y: 10 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 0.7, delay: 0.4 }}
        className="absolute -left-1 top-44 md:left-2 md:top-56 glass rounded-xl px-3 py-2 flex items-center gap-2 shadow-glow"
      >
        <Sparkles className="h-4 w-4 text-cyan" />
        <span className="text-xs text-white">AI Assistant deployed</span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 30, y: 10 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 0.7, delay: 0.6 }}
        className="absolute right-0 top-60 md:right-2 md:top-72 glass rounded-xl px-3 py-2 flex items-center gap-2"
      >
        <Activity className="h-4 w-4 text-emerald-300" />
        <span className="text-xs text-white">Latency 38ms</span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.8 }}
        className="absolute left-3 bottom-3 md:left-6 md:bottom-5 glass rounded-2xl p-3.5 w-[54%] md:w-[52%]"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 shrink-0 rounded-md grid place-items-center bg-cyan/15 border border-cyan/30">
              <Zap className="h-4 w-4 text-cyan" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] text-ink-400">Automation</div>
              <div className="text-xs md:text-sm text-white font-semibold truncate">Invoice → ERP sync</div>
            </div>
          </div>
          <div className="text-[10px] font-mono text-emerald-300 shrink-0">live</div>
        </div>
        <div className="mt-2.5 h-1.5 rounded-full bg-white/5 overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-royal to-cyan"
            initial={{ width: "0%" }}
            animate={{ width: ["10%", "85%", "62%", "92%"] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 1 }}
        className="absolute right-3 bottom-3 md:right-6 md:bottom-5 glass rounded-xl p-3 w-36 md:w-40"
      >
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-cyan" />
          <div className="text-xs text-white">Conversion</div>
        </div>
        <div className="mt-1.5 text-2xl font-bold text-white">2.4×</div>
        <div className="text-[10px] text-ink-400">vs. baseline</div>
      </motion.div>
    </div>
  );
}
